import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "../../contexts/CartContext";
import DOMPurify from "dompurify";
import getImageUrl from "../../utils/getImageUrl";
import api from "../../utils/apis";
import {
  Truck,
  MapPin,
  Zap,
  X,
  Check,
  Minus,
  Plus,
  Star,
  TrendingUp,
  ShoppingBag,
} from "lucide-react";
import SealBadge from './SealBadge';

const ProductCard = ({ product }) => {
  const { cart, addToCart } = useContext(CartContext);
  const [showModal, setShowModal] = useState(false);
  const [selectedVar, setSelectedVar] = useState(null);
  const [qty, setQty] = useState(1);
  const [fetchedVariations, setFetchedVariations] = useState([]);
  const [loadingVars, setLoadingVars] = useState(false);
  const navigate = useNavigate();

  const {
    id,
    title,
    product_img,
    product_thumb_nail,
    vendor_name,
    short_description,
    is_featured,
    is_trusted,
    is_mexa_verified,
    is_premium,
    is_trending,
    store,
    offer,
    variations,
    slag,
    brand,
    subcategory_name,
    price,
    mrp,
  } = product || {};

  // Helper to safely check boolean flags from API (handles "0", 0, false, "1", 1, true)
  const isTrue = (val) => val === 1 || val === '1' || val === true;

  const isAdded = cart.some((item) => item.productId === String(id));

  const allVariations = variations || fetchedVariations || [];

  useEffect(() => {
    setSelectedVar(allVariations[0] || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, variations]);

  const rawHtmlDescription = short_description?.replace(/\\"/g, '"')?.trim();

  const vendorDisplay = vendor_name || store?.name || "Verified Supplier";
  const categoryTag = brand || subcategory_name || "PRODUCT";

  const handleOpenModal = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    let currentVars = allVariations;

    if (currentVars.length === 0) {
      try {
        setLoadingVars(true);
        const response = await api.get(`/products/${id}`);
        if (response.data && response.data.variations) {
          currentVars = response.data.variations;
          setFetchedVariations(response.data.variations);
        }
      } catch (err) {
        console.error("Error fetching variations:", err);
      } finally {
        setLoadingVars(false);
      }
    }

    if (currentVars.length > 1) {
      setSelectedVar(currentVars[0]);
      setQty(1);
      setShowModal(true);
    } else if (currentVars.length === 1) {
      const targetVar = currentVars[0];
      addToCart({
        productId: String(id),
        store_id: store?.id,
        title,
        product_image: product_img,
        vendorName: vendor_name,
        offer: offer || null,
        quantity: 1,
        variationId: targetVar.id,
        variationName: targetVar.name,
      });
    } else {
      addToCart({
        productId: String(id),
        store_id: store?.id,
        title,
        product_image: product_img,
        vendorName: vendor_name,
        offer: offer || null,
        quantity: 1,
        variationId: null,
        variationName: null,
      });
    }
  };

  const handleConfirm = (e) => {
    e.stopPropagation();
    if (!selectedVar) return;

    const finalQty = qty === "" || qty < 1 ? 1 : parseInt(qty);

    addToCart({
      productId: String(id),
      store_id: store?.id,
      title,
      product_image: product_img,
      vendorName: vendor_name,
      offer: offer || null,
      quantity: finalQty,
      variationId: selectedVar.id,
      variationName: selectedVar.name,
    });
    setShowModal(false);
    setQty(1);
  };

  const handleQtyChange = (e) => {
    const val = e.target.value;
    if (val === "") {
      setQty("");
    } else {
      const parsed = parseInt(val);
      if (!isNaN(parsed) && parsed > 0) {
        setQty(parsed);
      }
    }
  };

  const handleQtyBlur = () => {
    if (qty === "" || qty < 1) {
      setQty(1);
    }
  };

  return (
    <>
      <div
        onClick={() => navigate(`/product/${slag}`)}
        className="relative bg-white rounded-lg sm:rounded-lg border border-slate-200 sm:border-[#0F766E]/10 
             transition-all w-full flex flex-col justify-between overflow-hidden group h-full p-3 sm:p-4 cursor-pointer"
      >
        {/* ========================================================= */}
        {/* MOBILE VIEW (< sm) - Vertical Stack Card (EVERYTHING SHOWN) */}
        {/* ========================================================= */}
        <div className="flex flex-col justify-between h-full">
          {/* Top Section */}
          <div className="flex flex-col flex-1">
            {/* 1. Top Badges removed for UI */}
            {/* 2. Centered Image Section with Premium Badge */}
            <div className="w-full h-32 flex items-center justify-center p-2 mb-2 relative rounded-xl overflow-hidden">
              {(isTrue(is_premium) || isTrue(store?.is_premium)) && (
                <span className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 bg-white border border-[#0F766E]/10 text-[#0F766E] shadow-sm px-1.5 py-0.5 rounded-full text-[8.5px] font-bold uppercase tracking-wider">
                  <Zap size={10} className="text-current" />
                  <span>Premium</span>
                </span>
              )}

              {(isTrue(is_trusted) || isTrue(is_mexa_verified)) && (
                <div className="absolute bottom-2 right-2 z-10 flex justify-end gap-1 shrink-0">
                  {isTrue(is_trusted) && <SealBadge type="trusted" />}
                  {isTrue(is_mexa_verified) && <SealBadge type="verified" />}
                </div>
              )}

              <img
                src={getImageUrl(product_thumb_nail || product_img, "/images/productimgmiss.png")}
                alt={title}
                className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-200"
                onError={(e) => { e.target.onerror = null; e.target.src = "/images/productimgmiss.png"; }}
              />
            </div>

            {/* 3. Category & Brand Tag */}
            <div className="flex items-center gap-1.5 text-[10px] text-slate-500 mb-1 font-medium uppercase tracking-wider truncate">
              {brand && <span>{brand}</span>}
              {brand && subcategory_name && <span className="text-slate-300">•</span>}
              {subcategory_name && <span>{subcategory_name}</span>}
            </div>

            {/* 4. Title */}
            <Link to={`/product/${slag}`} className="block mb-1">
              <h3 className="text-xs sm:text-sm font-bold text-[#0F172A] leading-snug truncate hover:text-[#0F766E] transition-colors">
                {title}
              </h3>
            </Link>

            {/* 6. Description (Removed) */}

            {/* 7. Variations Badges */}
            <div className="flex gap-1 flex-nowrap overflow-hidden mb-2.5 mt-auto w-full">
              {allVariations.slice(0, 2).map((v, idx) => (
                <span key={v.id || idx} className="flex items-center gap-1 bg-slate-50 border border-slate-200 text-slate-600 text-[9px] font-bold px-1.5 py-0.5 rounded-sm uppercase tracking-wide whitespace-nowrap min-w-0 shrink">
                  <span className="truncate">{v.name}</span>
                </span>
              ))}
              {allVariations.length > 2 && (
                <span className="flex items-center gap-1 bg-slate-50 border border-slate-200 text-slate-600 text-[9px] font-bold px-1.5 py-0.5 rounded-sm uppercase tracking-wide whitespace-nowrap shrink-0">
                  +{allVariations.length - 2} more
                </span>
              )}
            </div>
          </div>

          {/* 8. Mobile Footer (Logistics + Price + Button) */}
          <div className="mt-auto pt-2 flex flex-col gap-2 relative border-t border-slate-100 shrink-0">
            {/* Logistics */}
            <div className="flex items-center gap-2 text-[9.5px] text-[#64748B]">

              {offer && (offer.type || offer.title) && (
                <div className="border-l-2 border-[#EF4444] p-0.5 pl-1.5">
                  <span className="text-[7.5px] font-black text-[#EF4444] block leading-none mb-0.5 uppercase tracking-widest">
                    {offer.type || "OFFER"}
                  </span>
                  <p className="text-[9.5px] font-bold text-[#0F172A] truncate">
                    {offer.title}
                  </p>
                </div>
              )}

            </div>

            {/* Price & Action Button */}
            <div className="flex items-center justify-between gap-1.5 pt-0.5">
           
             
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenModal(e);
                }}
                disabled={loadingVars}
                className={`ml-auto text-[10px] font-bold py-1.5 px-3 rounded-lg transition-all shadow-sm active:scale-95 whitespace-nowrap flex items-center gap-1 ${isAdded ? "bg-[#1A936F] text-white" : "bg-[#0F766E] text-white hover:bg-[#0D655E]"
                  }`}
              >
                {loadingVars ? (
                  "Loading..."
                ) : isAdded ? (
                  <>
                    <Check size={12} />
                    Added
                  </>
                ) : (
                  "Enquire Now"
                )}
              </button>
            </div>
          </div>
        </div>

      </div>

      {showModal && (
        <div
          className="fixed inset-0 z-[999] flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-0 sm:p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white w-full sm:w-auto sm:min-w-[400px] sm:max-w-[400px] rounded-t-2xl sm:rounded-2xl shadow-2xl animate-in fade-in slide-in-from-bottom-10 duration-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-slate-100 flex justify-between items-center">
              <h4 className="font-semibold text-slate-900">Configure Item</h4>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-slate-800 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {allVariations.length > 0 && (
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">
                    Select Option
                  </label>
                  <div className="space-y-2">
                    {allVariations.map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => setSelectedVar(v)}
                        className={`w-full flex items-center justify-between p-3 rounded-lg border text-sm transition-all ${selectedVar?.id === v.id
                          ? "border-slate-900 bg-slate-50 text-slate-900 font-medium"
                          : "border-slate-200 text-slate-600 hover:border-slate-400"
                          }`}
                      >
                        <span className="flex items-center gap-2">
                          <span>{v.name}</span>

                        </span>

                        {selectedVar?.id === v.id && (
                          <Check size={16} className="text-slate-900" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">
                  Quantity
                </label>
                <div className="flex items-center gap-3">
                  <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() =>
                        setQty(Math.max(1, (parseInt(qty) || 1) - 1))
                      }
                      className="p-3 hover:bg-slate-50 text-slate-600 border-r border-slate-200 transition-colors"
                    >
                      <Minus size={16} />
                    </button>

                    <input
                      type="number"
                      min="1"
                      value={qty}
                      onChange={handleQtyChange}
                      onBlur={handleQtyBlur}
                      className="w-16 text-center text-sm font-medium text-slate-900 focus:outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />

                    <button
                      onClick={() => setQty((parseInt(qty) || 0) + 1)}
                      className="p-3 hover:bg-slate-50 text-slate-600 border-l border-slate-200 transition-colors"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-slate-100 bg-slate-50/50">
              <button
                onClick={handleConfirm}
                disabled={!selectedVar}
                className={`w-full py-3 rounded-lg font-medium text-sm transition-colors ${selectedVar
                  ? "bg-slate-900 hover:bg-slate-800 text-white"
                  : "bg-slate-200 text-slate-400 cursor-not-allowed"
                  }`}
              >
                Confirm & Add
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCard;

